import { api } from './client';

export type SkillCategory =
  | 'generate'
  | 'analyze'
  | 'organize'
  | 'predict'
  | 'optimize'
  | 'collaborate';

export type SkillTrigger = 'manual' | 'time' | 'event' | 'pattern' | 'threshold';

export interface Skill {
  id: string;
  name: string;
  category: SkillCategory;
  description: string;
  agent: string;
  trigger: SkillTrigger;
  endpoint: string;
  method: string;
  params?: Record<string, unknown>;
  capabilities?: string[];
  confidence: number;
  is_enabled: boolean;
  risk_level: number;
  icon?: string;
  frontend_route?: string;
  created_at: string;
  updated_at: string;
}

export interface SkillExecutionResult {
  skill_id: string;
  status: 'success' | 'failed' | 'skipped';
  message?: string;
  data?: Record<string, unknown>;
  executed_at: string;
}

export interface SkillListResponse {
  skills: Skill[];
  count: number;
}

export interface SkillCategoriesResponse {
  categories: Record<string, number>;
  total_skills: number;
}

export interface SkillFilter {
  category?: SkillCategory;
  agent?: string;
  enabled?: boolean;
  search?: string;
}

function buildQuery(filter: SkillFilter): string {
  const params = new URLSearchParams();
  if (filter.category) params.set('category', filter.category);
  if (filter.agent) params.set('agent', filter.agent);
  if (filter.enabled !== undefined) params.set('enabled', String(filter.enabled));
  if (filter.search) params.set('search', filter.search);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const skillService = {
  async listSkills(filter: SkillFilter = {}): Promise<Skill[]> {
    const res = await api.get<SkillListResponse>(`/skills${buildQuery(filter)}`);
    return res.data?.skills ?? [];
  },

  async getSkill(id: string): Promise<Skill | null> {
    try {
      const res = await api.get<Skill>(`/skills/${encodeURIComponent(id)}`);
      return res.data ?? null;
    } catch {
      return null;
    }
  },

  async executeSkill(id: string, params: Record<string, unknown> = {}): Promise<SkillExecutionResult> {
    const res = await api.post<SkillExecutionResult>(
      `/skills/${encodeURIComponent(id)}/execute`,
      params,
    );
    return res.data;
  },

  async enableSkill(id: string): Promise<Skill> {
    const res = await api.post<Skill>(`/skills/${encodeURIComponent(id)}/enable`);
    return res.data;
  },

  async disableSkill(id: string): Promise<Skill> {
    const res = await api.post<Skill>(`/skills/${encodeURIComponent(id)}/disable`);
    return res.data;
  },

  async getCategories(): Promise<SkillCategoriesResponse> {
    const res = await api.get<SkillCategoriesResponse>('/skills/categories');
    return res.data;
  },

  async getMarkdown(): Promise<string> {
    const res = await api.get<string>('/skills/markdown', { responseType: 'text' });
    return typeof res.data === 'string' ? res.data : String(res.data ?? '');
  },
};
